import { getQuestionnaires as getQuestionnairesBase } from "@common/actions/questionnaires";
import { sendInvite } from "@common/actions/invites";
import { NEW_INVITE_STEPS } from "@common/components/new-invite";
import { getAllMembersForISS, showFamilyQuestionnairesForMultiInvites, createSpreadsheetMembersRequest } from "../../../actions/batch-invites";
import { I18n } from "react-redux-i18n";
import * as React from "react";
import { BatchInvitesControllerProps, InviteData, SendInviteResponse } from "./batch-invites.types";
import "./batch-invites.styles.scss";

export const useBatchInvitesController = (props: BatchInvitesControllerProps) => {
  const { spreadsheetId, folderId, familyId = 0, queryModel, filterModel, showErrorMessage, showInfoMessage, showMessage, getFolderId, getCurrentQueryModel, getCurrentFilterModel } = props;
  
  const [batchInvitesOpened, setBatchInvitesOpenedState] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [selectedGlobalIds, setSelectedGlobalIds] = React.useState<number[]>([]);
  const [showFamilyQuestionnaires, setShowFamilyQuestionnaires] = React.useState(false);

  const openBatchInvites = React.useCallback(async () => {
    try {
      setLoading(true);

      const currentFolderId = getFolderId ? getFolderId() : folderId;
      
      // Step 1: Get all members from current spreadsheet view
      // Get current query model and filter model from table controller
      const currentQueryModel = getCurrentQueryModel ? getCurrentQueryModel() : queryModel;
      const currentFilterModel = getCurrentFilterModel ? getCurrentFilterModel() : filterModel;
      
      const request = createSpreadsheetMembersRequest(currentFolderId, familyId, currentQueryModel, currentFilterModel);
      const globalIds = await getAllMembersForISS(request);
      
      if (globalIds.length === 0) {
        showErrorMessage(I18n.t("No individuals found in the current spreadsheet view"));
        return;
      }
      
      setSelectedGlobalIds(globalIds);
      
      // Step 3: Check if family questionnaires should be shown
      const showFamily = await showFamilyQuestionnairesForMultiInvites(globalIds);
      setShowFamilyQuestionnaires(showFamily);
      
      setBatchInvitesOpenedState(true);
    } catch (error) {
      console.error("Failed to load spreadsheet individuals:", error);
      // Extract the actual error message from the backend response
      const errorMessage = (error as any)?.entity || (error as any)?.message || (error as Error)?.message || I18n.t("Failed to load individuals from spreadsheet");
      showErrorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [folderId, familyId, filterModel, showErrorMessage, getFolderId, getCurrentQueryModel, getCurrentFilterModel]);

  const closeBatchInvites = React.useCallback(() => {
    setBatchInvitesOpenedState(false);
    setSelectedGlobalIds([]);
    setShowFamilyQuestionnaires(false);
  }, []);

  const getBatchNewInvitesSteps = () => {
    return [
      {
        condition: true,
        step: NEW_INVITE_STEPS.questionnaires,
      },
      {
        condition: true,
        step: NEW_INVITE_STEPS.emailTemplate,
      }
    ];
  };

  const getQuestionnaires = () => {
    return getQuestionnairesBase({
      includeFamilyQuestionnaires: showFamilyQuestionnaires,
      includeFhq4Questionnaires: true,
      includeHiddenQuestionnaires: false
    });
  };

  const sendBatchInvites = async (inviteData: InviteData) => {
    try {
      setLoading(true);
      const response = await sendInvite({
        questionnaireId: inviteData.questionnaireId,
        fhq4: inviteData.fhq4,
        globalIds: selectedGlobalIds,
        emailSubject: inviteData.emailTemplate.subject,
        emailBody: inviteData.emailTemplate.body,
      });
      
      // Handle response with information about failed individuals
      const inviteResponse = response as SendInviteResponse;
      if (inviteResponse.data && inviteResponse.data.length > 0) {
        const messageContent = React.createElement('div', { className: 'invite-error-message' },
          React.createElement('p', null, I18n.t("The Questionnaire invitation emails were successfully submitted for all individuals with a valid email address")),
          React.createElement('p', null, I18n.t("There was a problem with the email addresses for the following individuals")),
          React.createElement('ul', { className: 'invite-error-list' },
            inviteResponse.data.map((name) => 
              React.createElement('li', { key: name, className: 'invite-error-item' }, name)
            )
          )
        );
        
        showMessage({
          header: I18n.t('Information Message'),
          content: messageContent,
          confirmText: I18n.t('Ok'),
        });
      } else {
        showInfoMessage(I18n.t("The Questionnaire invitation emails were successfully submitted"));
      }
      
      closeBatchInvites();
    } catch (error) {
      const errorMessage = (error as any)?.entity || (error as any)?.message || (error as Error)?.message || I18n.t("Failed to send invitations");
      showErrorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    getQuestionnaires,
    batchInvitesOpened,
    openBatchInvites,
    closeBatchInvites,
    getBatchNewInvitesSteps,
    sendBatchInvites,
    loading,
    selectedGlobalIds,
    showFamilyQuestionnaires,
  };
};